import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { 
    CalendarDays, Plus, Clock, User, Phone, Mail, MapPin, 
    ChevronLeft, ChevronRight, Loader2, Trash2, Edit, Check, X,
    Search, UserPlus, Users, List, LayoutGrid, Download, Filter,
    CalendarPlus, Send
} from 'lucide-react';
import { apiRequest, formatDate } from '../../lib/utils';
import { toast } from 'sonner';
import { format, addDays, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, parseISO, addWeeks, subWeeks, isAfter, isBefore, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';

const SERVICE_TYPES = [
    { id: 'leak_detection', name: 'Recherche de fuite', color: 'bg-red-500' },
    { id: 'diagnostic', name: 'Diagnostic piscine', color: 'bg-blue-500' },
    { id: 'repair', name: 'Réparation', color: 'bg-orange-500' },
    { id: 'maintenance', name: 'Entretien', color: 'bg-green-500' },
    { id: 'installation', name: 'Installation', color: 'bg-purple-500' },
    { id: 'renovation', name: 'Rénovation', color: 'bg-pink-500' },
    { id: 'other', name: 'Autre', color: 'bg-gray-500' }
];

const TIME_SLOTS = [
    '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30'
];

const STATUS_COLORS = {
    pending: 'bg-yellow-500',
    confirmed: 'bg-green-500',
    completed: 'bg-blue-500',
    cancelled: 'bg-red-500'
};

const STATUS_LABELS = {
    pending: 'En attente',
    confirmed: 'Confirmé',
    completed: 'Terminé',
    cancelled: 'Annulé'
};

export default function AgendaSection() {
    const [appointments, setAppointments] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    
    // View mode: 'calendar' or 'list'
    const [viewMode, setViewMode] = useState('list');
    // Filter for all appointments list
    const [listFilter, setListFilter] = useState('all'); // 'all', 'upcoming', 'past', 'pending', 'confirmed'
    const [listSearchQuery, setListSearchQuery] = useState('');
    
    // Form state
    const [formData, setFormData] = useState({
        clientType: 'new', // 'new' or 'existing'
        userId: '',
        name: '',
        email: '',
        phone: '',
        service: '',
        date: null,
        time: '',
        address: '',
        notes: '',
        status: 'confirmed'
    });
    const [submitting, setSubmitting] = useState(false);
    const [showSubscribeDialog, setShowSubscribeDialog] = useState(false);
    const [subscribeInfo, setSubscribeInfo] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [appointmentsData, usersData] = await Promise.all([
                apiRequest('/appointments/admin/all'),
                apiRequest('/admin/users')
            ]);
            setAppointments(appointmentsData || []);
            setUsers(usersData || []);
        } catch (error) {
            console.error('Failed to load agenda data:', error);
            toast.error('Erreur lors du chargement de l\'agenda');
        } finally {
            setLoading(false);
        }
    };

    const weekDays = eachDayOfInterval({
        start: currentWeekStart,
        end: endOfWeek(currentWeekStart, { weekStartsOn: 1 })
    });

    const navigateWeek = (direction) => {
        if (direction === 'prev') {
            setCurrentWeekStart(subWeeks(currentWeekStart, 1));
        } else {
            setCurrentWeekStart(addWeeks(currentWeekStart, 1));
        }
    };

    const goToToday = () => {
        setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
    };

    const getAppointmentsForDay = (day) => {
        return appointments.filter(apt => {
            const aptDate = parseISO(apt.date);
            return isSameDay(aptDate, day);
        });
    };

    const getServiceColor = (serviceId) => {
        const service = SERVICE_TYPES.find(s => s.id === serviceId);
        return service?.color || 'bg-gray-500';
    };

    const getServiceName = (serviceId) => {
        const service = SERVICE_TYPES.find(s => s.id === serviceId);
        return service?.name || serviceId;
    };

    const openAddDialog = (date = null) => {
        setSelectedDate(date);
        setFormData({
            clientType: 'new',
            userId: '',
            name: '',
            email: '',
            phone: '',
            service: '',
            date: date,
            time: '',
            address: '',
            notes: '',
            status: 'confirmed'
        });
        setSelectedAppointment(null);
        setShowAddDialog(true);
    };

    const openEditDialog = (appointment) => {
        setSelectedAppointment(appointment);
        setFormData({
            clientType: appointment.user_id ? 'existing' : 'new',
            userId: appointment.user_id || '',
            name: appointment.name || '',
            email: appointment.email || '',
            phone: appointment.phone || '',
            service: appointment.service || '',
            date: parseISO(appointment.date),
            time: appointment.time || '',
            address: appointment.address || '',
            notes: appointment.notes || '',
            status: appointment.status || 'pending'
        });
        setShowAddDialog(true);
    };

    const handleUserSelect = (userId) => {
        const user = users.find(u => u.user_id === userId);
        if (user) {
            setFormData(prev => ({
                ...prev,
                userId: userId,
                name: user.name || '',
                email: user.email || '',
                phone: user.phone || ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name || !formData.phone || !formData.service || !formData.date || !formData.time) {
            toast.error('Veuillez remplir tous les champs obligatoires');
            return;
        }

        setSubmitting(true);
        try {
            const appointmentData = {
                user_id: formData.clientType === 'existing' ? formData.userId : null,
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                service: formData.service,
                date: format(formData.date, 'yyyy-MM-dd'),
                time: formData.time,
                address: formData.address,
                notes: formData.notes,
                status: formData.status
            };

            if (selectedAppointment) {
                await apiRequest(`/appointments/admin/${selectedAppointment.appointment_id}`, {
                    method: 'PUT',
                    body: JSON.stringify(appointmentData)
                });
                toast.success('Rendez-vous modifié');
            } else {
                await apiRequest('/appointments/admin/create', {
                    method: 'POST',
                    body: JSON.stringify(appointmentData)
                });
                toast.success('Rendez-vous créé');
            }

            setShowAddDialog(false);
            loadData();
        } catch (error) {
            console.error('Error saving appointment:', error);
            toast.error('Erreur lors de l\'enregistrement');
        } finally {
            setSubmitting(false);
        }
    };

    const handleStatusChange = async (appointmentId, newStatus) => {
        try {
            await apiRequest(`/appointments/admin/${appointmentId}/status`, {
                method: 'PUT',
                body: JSON.stringify({ status: newStatus })
            });
            toast.success('Statut mis à jour');
            loadData();
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Erreur lors de la mise à jour');
        }
    };

    const handleDelete = async (appointmentId) => {
        if (!confirm('Êtes-vous sûr de vouloir supprimer ce rendez-vous ?')) return;
        
        try {
            await apiRequest(`/appointments/admin/${appointmentId}`, { method: 'DELETE' });
            toast.success('Rendez-vous supprimé');
            loadData();
        } catch (error) {
            console.error('Error deleting appointment:', error);
            toast.error('Erreur lors de la suppression');
        }
    };

    const handleDownloadIcs = async (appointmentId) => {
        try {
            const token = localStorage.getItem('auth_token');
            const baseUrl = process.env.REACT_APP_BACKEND_URL;
            const res = await fetch(`${baseUrl}/api/appointments/admin/${appointmentId}/ics`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Download failed');
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `rdv_${appointmentId}.ics`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success('Fichier calendrier téléchargé');
        } catch (error) {
            console.error('Error downloading ics:', error);
            toast.error('Erreur lors du téléchargement');
        }
    };

    const handleResendEmail = async (appointmentId) => {
        if (!confirm('Renvoyer l\'email au client avec le fichier calendrier ?')) return;
        try {
            await apiRequest(`/appointments/admin/${appointmentId}/resend-email`, { method: 'POST' });
            toast.success('Email renvoyé au client');
        } catch (error) {
            console.error('Error resending email:', error);
            toast.error(error.message || 'Erreur lors de l\'envoi');
        }
    };

    const loadSubscribeInfo = async () => {
        try {
            const data = await apiRequest('/appointments/admin/calendar-feed-url');
            setSubscribeInfo(data);
        } catch (error) {
            console.error('Error loading subscribe info:', error);
            toast.error('Impossible de charger l\'abonnement');
        }
    };

    useEffect(() => {
        if (showSubscribeDialog && !subscribeInfo) {
            loadSubscribeInfo();
        }
    }, [showSubscribeDialog]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleRegenerateToken = async () => {
        if (!confirm('Régénérer le lien ? L\'ancien lien cessera de fonctionner immédiatement.')) return;
        try {
            const data = await apiRequest('/appointments/admin/calendar-feed-regenerate', { method: 'POST' });
            setSubscribeInfo(data);
            toast.success('Nouveau lien généré');
        } catch (error) {
            toast.error('Erreur lors de la régénération');
        }
    };

    const handleCopyUrl = (url) => {
        navigator.clipboard.writeText(url).then(() => {
            toast.success('Lien copié');
        }).catch(() => {
            toast.error('Copie impossible');
        });
    };

    const filteredUsers = users.filter(user => 
        user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.phone?.includes(searchQuery)
    );

    // Filter appointments for the "all appointments" list view
    const getFilteredAppointments = () => {
        const today = startOfDay(new Date());
        let filtered = [...appointments];
        
        // Filter by status/time
        switch (listFilter) {
            case 'upcoming':
                filtered = filtered.filter(apt => {
                    const aptDate = parseISO(apt.date);
                    return isAfter(aptDate, today) || isSameDay(aptDate, today);
                });
                break;
            case 'past':
                filtered = filtered.filter(apt => {
                    const aptDate = parseISO(apt.date);
                    return isBefore(aptDate, today);
                });
                break;
            case 'pending':
                filtered = filtered.filter(apt => apt.status === 'pending');
                break;
            case 'confirmed':
                filtered = filtered.filter(apt => apt.status === 'confirmed');
                break;
            default:
                break;
        }
        
        // Filter by search query
        if (listSearchQuery) {
            const query = listSearchQuery.toLowerCase();
            filtered = filtered.filter(apt => 
                apt.name?.toLowerCase().includes(query) ||
                apt.phone?.includes(query) ||
                apt.email?.toLowerCase().includes(query) ||
                getServiceName(apt.service)?.toLowerCase().includes(query)
            );
        }
        
        // Sort by date (newest first for past, soonest first for upcoming/all)
        filtered.sort((a, b) => {
            const dateA = parseISO(a.date);
            const dateB = parseISO(b.date);
            if (listFilter === 'past') {
                return dateB - dateA; // Most recent past first
            }
            return dateA - dateB; // Soonest first
        });
        
        return filtered;
    };

    const filteredAppointmentsList = getFilteredAppointments();

    // Export CSV function
    const handleExportCSV = () => {
        const headers = ['Date', 'Heure', 'Nom', 'Téléphone', 'Email', 'Service', 'Adresse', 'Statut', 'Notes'];
        const rows = filteredAppointmentsList.map(apt => [
            apt.date,
            apt.time || '',
            apt.name || '',
            apt.phone || '',
            apt.email || '',
            getServiceName(apt.service) || '',
            apt.address || '',
            STATUS_LABELS[apt.status] || apt.status,
            apt.notes || ''
        ]);
        
        const csvContent = [
            headers.join(';'),
            ...rows.map(row => row.map(cell => `"${(cell || '').replace(/"/g, '""')}"`).join(';'))
        ].join('\n');
        
        const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rendez-vous_oca_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Export CSV téléchargé');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold">Agenda</h2>
                    <p className="text-muted-foreground">Gérez vos rendez-vous clients ({appointments.length} au total)</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {/* View Toggle */}
                    <div className="flex rounded-full border overflow-hidden">
                        <Button 
                            variant={viewMode === 'list' ? 'default' : 'ghost'} 
                            size="sm"
                            className="rounded-none gap-1"
                            onClick={() => setViewMode('list')}
                        >
                            <List className="h-4 w-4" />
                            <span className="hidden sm:inline">Liste</span>
                        </Button>
                        <Button 
                            variant={viewMode === 'calendar' ? 'default' : 'ghost'} 
                            size="sm"
                            className="rounded-none gap-1"
                            onClick={() => setViewMode('calendar')}
                        >
                            <LayoutGrid className="h-4 w-4" />
                            <span className="hidden sm:inline">Calendrier</span>
                        </Button>
                    </div>
                    <Button onClick={() => openAddDialog()} className="rounded-full gap-2">
                        <Plus className="h-4 w-4" />
                        <span className="hidden sm:inline">Nouveau</span>
                    </Button>
                    <Button variant="outline" onClick={() => setShowSubscribeDialog(true)} className="rounded-full gap-2" data-testid="subscribe-calendar-btn">
                        <CalendarPlus className="h-4 w-4" />
                        <span className="hidden sm:inline">S'abonner</span>
                    </Button>
                </div>
            </div>

            {/* LIST VIEW - All Appointments at a Glance */}
            {viewMode === 'list' && (
                <Card className="glass rounded-2xl">
                    <CardHeader className="pb-4">
                        <div className="flex flex-col lg:flex-row justify-between gap-4">
                            <CardTitle className="flex items-center gap-2">
                                <CalendarDays className="h-5 w-5 text-primary" />
                                Tous les rendez-vous ({filteredAppointmentsList.length})
                            </CardTitle>
                            <div className="flex flex-wrap items-center gap-2">
                                {/* Search */}
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Rechercher..."
                                        value={listSearchQuery}
                                        onChange={(e) => setListSearchQuery(e.target.value)}
                                        className="pl-10 w-48 rounded-full"
                                    />
                                </div>
                                {/* Filter */}
                                <Select value={listFilter} onValueChange={setListFilter}>
                                    <SelectTrigger className="w-36 rounded-full">
                                        <Filter className="h-4 w-4 mr-2" />
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tous</SelectItem>
                                        <SelectItem value="upcoming">À venir</SelectItem>
                                        <SelectItem value="past">Passés</SelectItem>
                                        <SelectItem value="pending">En attente</SelectItem>
                                        <SelectItem value="confirmed">Confirmés</SelectItem>
                                    </SelectContent>
                                </Select>
                                {/* Export */}
                                <Button variant="outline" size="sm" className="rounded-full gap-1" onClick={handleExportCSV}>
                                    <Download className="h-4 w-4" />
                                    <span className="hidden sm:inline">CSV</span>
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {filteredAppointmentsList.length === 0 ? (
                            <p className="text-muted-foreground text-center py-8">Aucun rendez-vous trouvé</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b text-left">
                                            <th className="pb-3 font-semibold">Date</th>
                                            <th className="pb-3 font-semibold">Heure</th>
                                            <th className="pb-3 font-semibold">Client</th>
                                            <th className="pb-3 font-semibold hidden md:table-cell">Téléphone</th>
                                            <th className="pb-3 font-semibold">Service</th>
                                            <th className="pb-3 font-semibold">Statut</th>
                                            <th className="pb-3 font-semibold text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        {filteredAppointmentsList.map((apt) => {
                                            const aptDate = parseISO(apt.date);
                                            const isPast = isBefore(aptDate, startOfDay(new Date()));
                                            const isToday = isSameDay(aptDate, new Date());
                                            
                                            return (
                                                <tr 
                                                    key={apt.appointment_id} 
                                                    className={`hover:bg-muted/50 transition-colors ${isPast ? 'opacity-60' : ''} ${isToday ? 'bg-primary/5' : ''}`}
                                                >
                                                    <td className="py-3">
                                                        <div className="flex items-center gap-2">
                                                            <div className={`w-2 h-8 rounded-full ${getServiceColor(apt.service)}`} />
                                                            <div>
                                                                <p className="font-medium">
                                                                    {format(aptDate, 'dd/MM/yyyy')}
                                                                </p>
                                                                <p className="text-xs text-muted-foreground">
                                                                    {format(aptDate, 'EEEE', { locale: fr })}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="py-3">
                                                        <span className="font-mono">{apt.time || '-'}</span>
                                                    </td>
                                                    <td className="py-3">
                                                        <div>
                                                            <p className="font-medium">{apt.name}</p>
                                                            {apt.email && (
                                                                <p className="text-xs text-muted-foreground truncate max-w-[150px]">
                                                                    {apt.email}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="py-3 hidden md:table-cell">
                                                        {apt.phone ? (
                                                            <a href={`tel:${apt.phone}`} className="text-primary hover:underline">
                                                                {apt.phone}
                                                            </a>
                                                        ) : '-'}
                                                    </td>
                                                    <td className="py-3">
                                                        <Badge variant="secondary" className="whitespace-nowrap">
                                                            {getServiceName(apt.service)}
                                                        </Badge>
                                                    </td>
                                                    <td className="py-3">
                                                        <Select 
                                                            value={apt.status} 
                                                            onValueChange={(value) => handleStatusChange(apt.appointment_id, value)}
                                                        >
                                                            <SelectTrigger className="w-28 h-8 rounded-full text-xs">
                                                                <Badge className={`${STATUS_COLORS[apt.status]} text-white text-xs`}>
                                                                    {STATUS_LABELS[apt.status]}
                                                                </Badge>
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="pending">En attente</SelectItem>
                                                                <SelectItem value="confirmed">Confirmé</SelectItem>
                                                                <SelectItem value="completed">Terminé</SelectItem>
                                                                <SelectItem value="cancelled">Annulé</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    </td>
                                                    <td className="py-3 text-right">
                                                        <div className="flex justify-end gap-1">
                                                            <Button 
                                                                variant="ghost" 
                                                                size="icon" 
                                                                className="h-8 w-8 text-sky-600"
                                                                title="Télécharger pour mon agenda"
                                                                onClick={() => handleDownloadIcs(apt.appointment_id)}
                                                                data-testid={`ics-download-${apt.appointment_id}`}
                                                            >
                                                                <CalendarPlus className="h-4 w-4" />
                                                            </Button>
                                                            <Button 
                                                                variant="ghost" 
                                                                size="icon" 
                                                                className="h-8 w-8 text-emerald-600"
                                                                title="Renvoyer l'email au client"
                                                                onClick={() => handleResendEmail(apt.appointment_id)}
                                                                data-testid={`resend-email-${apt.appointment_id}`}
                                                            >
                                                                <Send className="h-4 w-4" />
                                                            </Button>
                                                            <Button 
                                                                variant="ghost" 
                                                                size="icon" 
                                                                className="h-8 w-8"
                                                                onClick={() => openEditDialog(apt)}
                                                            >
                                                                <Edit className="h-4 w-4" />
                                                            </Button>
                                                            <Button 
                                                                variant="ghost" 
                                                                size="icon" 
                                                                className="h-8 w-8 text-destructive"
                                                                onClick={() => handleDelete(apt.appointment_id)}
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* CALENDAR VIEW */}
            {viewMode === 'calendar' && (
                <>
            <Card className="glass rounded-2xl">
                <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="icon" onClick={() => navigateWeek('prev')} className="rounded-full">
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="icon" onClick={() => navigateWeek('next')} className="rounded-full">
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" onClick={goToToday} className="rounded-full">
                                Aujourd'hui
                            </Button>
                        </div>
                        <h3 className="font-semibold text-lg">
                            {format(currentWeekStart, 'd MMM', { locale: fr })} - {format(endOfWeek(currentWeekStart, { weekStartsOn: 1 }), 'd MMM yyyy', { locale: fr })}
                        </h3>
                    </div>

                    {/* Week Grid */}
                    <div className="grid grid-cols-7 gap-2">
                        {weekDays.map((day, index) => {
                            const dayAppointments = getAppointmentsForDay(day);
                            const isToday = isSameDay(day, new Date());
                            
                            return (
                                <div 
                                    key={index} 
                                    className={`min-h-[200px] border rounded-xl p-2 ${
                                        isToday ? 'bg-primary/5 border-primary' : 'bg-muted/30'
                                    }`}
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="text-center">
                                            <p className="text-xs text-muted-foreground">
                                                {format(day, 'EEE', { locale: fr })}
                                            </p>
                                            <p className={`text-lg font-bold ${isToday ? 'text-primary' : ''}`}>
                                                {format(day, 'd')}
                                            </p>
                                        </div>
                                        <Button 
                                            variant="ghost" 
                                            size="icon" 
                                            className="h-6 w-6 rounded-full"
                                            onClick={() => openAddDialog(day)}
                                        >
                                            <Plus className="h-3 w-3" />
                                        </Button>
                                    </div>
                                    
                                    <div className="space-y-1">
                                        {dayAppointments.map((apt) => (
                                            <div 
                                                key={apt.appointment_id}
                                                className={`p-1.5 rounded-lg text-white text-xs cursor-pointer hover:opacity-90 transition-opacity ${getServiceColor(apt.service)}`}
                                                onClick={() => openEditDialog(apt)}
                                            >
                                                <div className="font-medium truncate">{apt.time} - {apt.name}</div>
                                                <div className="truncate opacity-90">{getServiceName(apt.service)}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* Today's Appointments List */}
            <Card className="glass rounded-2xl">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <CalendarDays className="h-5 w-5 text-primary" />
                        Rendez-vous du jour ({getAppointmentsForDay(new Date()).length})
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {getAppointmentsForDay(new Date()).length === 0 ? (
                        <p className="text-muted-foreground text-center py-8">Aucun rendez-vous aujourd'hui</p>
                    ) : (
                        <div className="space-y-3">
                            {getAppointmentsForDay(new Date()).map((apt) => (
                                <div key={apt.appointment_id} className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                                    <div className="flex items-center gap-4">
                                        <div className={`w-2 h-12 rounded-full ${getServiceColor(apt.service)}`} />
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold">{apt.time}</span>
                                                <Badge className={`${STATUS_COLORS[apt.status]} text-white`}>
                                                    {STATUS_LABELS[apt.status]}
                                                </Badge>
                                            </div>
                                            <p className="font-medium">{apt.name}</p>
                                            <p className="text-sm text-muted-foreground">{getServiceName(apt.service)}</p>
                                            {apt.phone && (
                                                <p className="text-sm text-muted-foreground flex items-center gap-1">
                                                    <Phone className="h-3 w-3" /> {apt.phone}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Select value={apt.status} onValueChange={(value) => handleStatusChange(apt.appointment_id, value)}>
                                            <SelectTrigger className="w-32 rounded-full">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="pending">En attente</SelectItem>
                                                <SelectItem value="confirmed">Confirmé</SelectItem>
                                                <SelectItem value="completed">Terminé</SelectItem>
                                                <SelectItem value="cancelled">Annulé</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <Button variant="ghost" size="icon" className="text-sky-600" title="Télécharger pour mon agenda" onClick={() => handleDownloadIcs(apt.appointment_id)}>
                                            <CalendarPlus className="h-4 w-4" />
                                        </Button>
                                        <Button variant="ghost" size="icon" className="text-emerald-600" title="Renvoyer l'email au client" onClick={() => handleResendEmail(apt.appointment_id)}>
                                            <Send className="h-4 w-4" />
                                        </Button>
                                        <Button variant="ghost" size="icon" onClick={() => openEditDialog(apt)}>
                                            <Edit className="h-4 w-4" />
                                        </Button>
                                        <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(apt.appointment_id)}>
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
            </>
            )}

            {/* Add/Edit Dialog */}
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {selectedAppointment ? 'Modifier le rendez-vous' : 'Nouveau rendez-vous'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Client Type Selection */}
                        <div className="space-y-2">
                            <Label>Type de client</Label>
                            <div className="flex gap-2">
                                <Button
                                    type="button"
                                    variant={formData.clientType === 'new' ? 'default' : 'outline'}
                                    className="flex-1 rounded-full gap-2"
                                    onClick={() => setFormData(prev => ({ ...prev, clientType: 'new', userId: '' }))}
                                >
                                    <UserPlus className="h-4 w-4" />
                                    Nouveau client
                                </Button>
                                <Button
                                    type="button"
                                    variant={formData.clientType === 'existing' ? 'default' : 'outline'}
                                    className="flex-1 rounded-full gap-2"
                                    onClick={() => setFormData(prev => ({ ...prev, clientType: 'existing' }))}
                                >
                                    <Users className="h-4 w-4" />
                                    Client existant
                                </Button>
                            </div>
                        </div>

                        {/* Existing Client Search */}
                        {formData.clientType === 'existing' && (
                            <div className="space-y-2">
                                <Label>Rechercher un client</Label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Nom, email ou téléphone..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="pl-10 rounded-full"
                                    />
                                </div>
                                {searchQuery && (
                                    <div className="max-h-40 overflow-y-auto border rounded-xl">
                                        {filteredUsers.length === 0 ? (
                                            <p className="p-3 text-sm text-muted-foreground">Aucun client trouvé</p>
                                        ) : (
                                            filteredUsers.slice(0, 5).map(user => (
                                                <div 
                                                    key={user.user_id}
                                                    className={`p-3 cursor-pointer hover:bg-muted/50 ${formData.userId === user.user_id ? 'bg-primary/10' : ''}`}
                                                    onClick={() => {
                                                        handleUserSelect(user.user_id);
                                                        setSearchQuery('');
                                                    }}
                                                >
                                                    <p className="font-medium">{user.name}</p>
                                                    <p className="text-sm text-muted-foreground">{user.email}</p>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                                {formData.userId && (
                                    <Badge variant="secondary" className="mt-2">
                                        Client sélectionné: {formData.name}
                                    </Badge>
                                )}
                            </div>
                        )}

                        {/* Client Info */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Nom *</Label>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="Nom du client"
                                    className="rounded-full"
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Téléphone *</Label>
                                <Input
                                    value={formData.phone}
                                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                                    placeholder="06 12 34 56 78"
                                    className="rounded-full"
                                    required
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Email</Label>
                            <Input
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                                placeholder="email@exemple.com"
                                className="rounded-full"
                            />
                        </div>

                        {/* Service */}
                        <div className="space-y-2">
                            <Label>Type d'intervention *</Label>
                            <Select 
                                value={formData.service} 
                                onValueChange={(value) => setFormData(prev => ({ ...prev, service: value }))}
                                required
                            >
                                <SelectTrigger className="rounded-full">
                                    <SelectValue placeholder="Sélectionnez un service" />
                                </SelectTrigger>
                                <SelectContent>
                                    {SERVICE_TYPES.map(service => (
                                        <SelectItem key={service.id} value={service.id}>
                                            <div className="flex items-center gap-2">
                                                <div className={`w-3 h-3 rounded-full ${service.color}`} />
                                                {service.name}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Date & Time */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Date *</Label>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button variant="outline" className="w-full justify-start rounded-full">
                                            <CalendarDays className="mr-2 h-4 w-4" />
                                            {formData.date ? format(formData.date, 'dd/MM/yyyy') : 'Choisir'}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0">
                                        <Calendar
                                            mode="single"
                                            selected={formData.date}
                                            onSelect={(date) => setFormData(prev => ({ ...prev, date }))}
                                            locale={fr}
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                            <div className="space-y-2">
                                <Label>Heure *</Label>
                                <Select 
                                    value={formData.time} 
                                    onValueChange={(value) => setFormData(prev => ({ ...prev, time: value }))}
                                >
                                    <SelectTrigger className="rounded-full">
                                        <SelectValue placeholder="Heure" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {TIME_SLOTS.map(time => (
                                            <SelectItem key={time} value={time}>{time}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Address */}
                        <div className="space-y-2">
                            <Label>Adresse d'intervention</Label>
                            <Input
                                value={formData.address}
                                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                                placeholder="123 Rue de la Piscine, 83000 Toulon"
                                className="rounded-full"
                            />
                        </div>

                        {/* Status */}
                        <div className="space-y-2">
                            <Label>Statut</Label>
                            <Select 
                                value={formData.status} 
                                onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                            >
                                <SelectTrigger className="rounded-full">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="pending">En attente</SelectItem>
                                    <SelectItem value="confirmed">Confirmé</SelectItem>
                                    <SelectItem value="completed">Terminé</SelectItem>
                                    <SelectItem value="cancelled">Annulé</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Notes */}
                        <div className="space-y-2">
                            <Label>Notes</Label>
                            <Textarea
                                value={formData.notes}
                                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                placeholder="Notes supplémentaires..."
                                rows={3}
                                className="rounded-xl"
                            />
                        </div>

                        {/* Submit */}
                        <div className="flex justify-end gap-2 pt-4">
                            <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)} className="rounded-full">
                                Annuler
                            </Button>
                            <Button type="submit" disabled={submitting} className="rounded-full">
                                {submitting ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                ) : null}
                                {selectedAppointment ? 'Modifier' : 'Créer'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Subscribe to Calendar Dialog */}
            <Dialog open={showSubscribeDialog} onOpenChange={setShowSubscribeDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <CalendarPlus className="h-5 w-5 text-primary" />
                            S'abonner au calendrier
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 text-sm">
                        <p>
                            Ajoutez <strong>ce lien dans votre calendrier iPhone</strong> : tous les rendez-vous
                            existants <u>et futurs</u> apparaîtront automatiquement, sans doublon, avec un
                            rappel 1 jour et 1 heure avant.
                        </p>

                        {!subscribeInfo ? (
                            <div className="flex items-center justify-center py-6">
                                <Loader2 className="h-5 w-5 animate-spin" />
                            </div>
                        ) : (
                            <>
                                <div className="p-4 rounded-xl bg-primary/10 border border-primary/20 space-y-3">
                                    <div>
                                        <p className="font-semibold mb-1">📱 iPhone (recommandé)</p>
                                        <p className="text-xs text-muted-foreground mb-2">
                                            Appuyez sur le bouton ci-dessous depuis votre iPhone :
                                        </p>
                                        <a
                                            href={subscribeInfo.webcal_url}
                                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground font-medium"
                                            data-testid="subscribe-webcal-link"
                                        >
                                            <CalendarPlus className="h-4 w-4" />
                                            Ajouter à mon calendrier
                                        </a>
                                    </div>
                                </div>

                                <div>
                                    <p className="font-semibold mb-1">🖥️ Google Calendar / Outlook</p>
                                    <p className="text-xs text-muted-foreground mb-2">
                                        Copiez le lien HTTPS ci-dessous et collez-le dans "Ajouter un agenda par URL" :
                                    </p>
                                    <div className="flex gap-2">
                                        <Input
                                            value={subscribeInfo.https_url}
                                            readOnly
                                            className="font-mono text-xs"
                                            data-testid="subscribe-https-url"
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => handleCopyUrl(subscribeInfo.https_url)}
                                            data-testid="copy-https-btn"
                                        >
                                            Copier
                                        </Button>
                                    </div>
                                </div>

                                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-800 dark:text-amber-300">
                                    <p className="font-semibold">⚠️ Ne partagez pas ce lien</p>
                                    <p>Il contient un token secret qui donne accès à tous vos RDV et leurs coordonnées.</p>
                                </div>

                                <div className="flex justify-between items-center pt-2">
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleRegenerateToken}
                                        className="text-xs text-destructive"
                                    >
                                        Régénérer le lien
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setShowSubscribeDialog(false)}
                                        className="rounded-full"
                                    >
                                        Fermer
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
